import { useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminDbStats, adminPurge, formatApiError } from "@/lib/api";

const PHRASE = "ОЧИСТИТИ";
const LABELS = {
  measurements: "Сирі вимірювання",
  measurements_10m: "Агреговані (10 хв)",
  state_events: "Журнал станів BMS",
  energy_daily: "Енергія по днях",
  topics: "Останні значення топіків",
  devices: "Прилади",
  groups: "Групи",
  users: "Користувачі",
};

const fmtBytes = (b) =>
  b > 1e9 ? `${(b / 1e9).toFixed(2)} ГБ` : b > 1e6 ? `${(b / 1e6).toFixed(1)} МБ` : `${(b / 1e3).toFixed(0)} КБ`;

function StatsTable({ stats }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="admin-db-stats">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-3">Колекція</th>
            <th className="px-4 py-3 text-right">Записів</th>
            <th className="px-4 py-3 text-right">На диску</th>
          </tr>
        </thead>
        <tbody>
          {stats.collections.map((c) => (
            <tr key={c.name} className="border-t border-slate-100" data-testid={`db-stat-${c.name}`}>
              <td className="px-4 py-2.5 text-slate-700">{LABELS[c.name] || c.name}</td>
              <td className="px-4 py-2.5 text-right font-mono">{c.count.toLocaleString("uk-UA")}</td>
              <td className="px-4 py-2.5 text-right font-mono">{fmtBytes(c.storage)}</td>
            </tr>
          ))}
          <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
            <td className="px-4 py-2.5">Всього</td>
            <td />
            <td className="px-4 py-2.5 text-right font-mono" data-testid="db-total-storage">
              {fmtBytes(stats.total_storage)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PurgeCard({ onDone }) {
  const [scope, setScope] = useState("telemetry");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await adminPurge(scope, confirm);
      toast.success(scope === "all" ? "Базу даних повністю очищено" : "Історію вимірювань очищено");
      setConfirm("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const opt = (id, title, desc) => (
    <label
      className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
        scope === id ? "border-red-300 bg-red-50/60" : "border-slate-200 hover:bg-slate-50"
      }`}
    >
      <input
        type="radio"
        data-testid={`purge-scope-${id}`}
        checked={scope === id}
        onChange={() => setScope(id)}
        className="mt-1 accent-red-600"
      />
      <span>
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="block text-xs text-slate-500">{desc}</span>
      </span>
    </label>
  );

  return (
    <div className="rounded-xl border border-red-200 bg-white p-5" data-testid="admin-purge-card">
      <div className="mb-4 flex items-center gap-2 text-red-700">
        <AlertTriangle size={18} />
        <span className="font-semibold">Очищення бази даних</span>
      </div>
      <div className="space-y-2">
        {opt("telemetry", "Тільки історія", "Видаляє всі вимірювання, графіки, журнал станів та енергію. Прилади, групи й користувачі залишаються.")}
        {opt("all", "Вся база", "Додатково видаляє всі прилади, групи та всіх користувачів, крім адміністраторів.")}
      </div>
      <p className="mt-4 text-xs text-slate-500">
        Дію не можна скасувати. Введіть <b className="font-mono text-red-600">{PHRASE}</b> для підтвердження:
      </p>
      <div className="mt-2 flex gap-2">
        <input
          data-testid="purge-confirm-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={`Введіть ${PHRASE}`}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-red-400"
        />
        <button
          data-testid="purge-submit-btn"
          disabled={busy || confirm.trim() !== PHRASE}
          onClick={run}
          className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
          Очистити
        </button>
      </div>
    </div>
  );
}

export function DatabaseTab() {
  const [stats, setStats] = useState(null);
  const load = () => adminDbStats().then(setStats).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Розмір бази</span>
          <button
            data-testid="db-stats-refresh-btn"
            onClick={load}
            className="flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-800"
          >
            <RefreshCw size={13} /> Оновити
          </button>
        </div>
        {stats ? <StatsTable stats={stats} /> : <Loader2 className="animate-spin text-slate-400" />}
      </div>
      <PurgeCard onDone={load} />
    </div>
  );
}
